import PropTypes from 'prop-types'
import React, { memo, useContext } from 'react'

import { Loader, Grid, Typography, DeviceStatusInfo } from '../../../components/common/index'
import { useToggle } from '../../../components/CustomHooks'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

const Catalog = ({ title, renderOptions, children, loading, className, noGutter, ...rest }) => {
    const catalogClass = util.joinClasses('catalog', noGutter && 'no-gutter', className)
    return (
        <div data-testid={'catalog'} className={catalogClass} {...rest}>
            <Loader loading={loading} inline={true} />
            <div data-testid={'catalog-header'} className={'catalog__header'}>
                <Typography
                    fontType={'medium'}
                    size={'md'}
                    className={'catalog__header__title'}
                    text={title}
                />
                {renderOptions ? renderOptions() : null}
            </div>
            {React.Children.toArray(children).map(child => React.cloneElement(child, {}))}
        </div>
    )
}

const CatalogGrid = ({ children, showNoResults }) => {
    const { strings } = useContext(LanguageContext)

    return (
        <div
            data-testid={'catalog-grid'}
            className={util.joinClasses('catalog__wrapper', showNoResults && 'show-no-results')}
        >
            {!showNoResults ? (
                <Grid className={'catalog-grid'}>{children}</Grid>
            ) : (
                <div className={'catalog__wrapper__show-no-results'}>
                    <Typography text={strings.SECTION_DASHBOARD_NO_RESULTS} fontType={'medium'} />
                </div>
            )}
        </div>
    )
}

const CatalogGridRow = ({ children, className, alignItems = 'center', ...rest }) => {
    const clName = util.joinClasses('catalog-grid__row', className)
    return (
        <Grid.Row
            data-testid={'catalog-grid-row'}
            className={clName}
            alignItems={alignItems}
            {...rest}
        >
            {React.Children.toArray(children).map(child => child)}
        </Grid.Row>
    )
}

const CatalogAccordion = ({ types, showTypes }) => {
    if (!types.length) {
        return null
    }
    return showTypes ? (
        <div className={'pd-5'}>
            {types.map((type, i) => (
                <DeviceStatusInfo
                    className={i > 0 ? 'mt-3' : 'mt-0'}
                    key={type.name}
                    {...type}
                    showName
                />
            ))}
        </div>
    ) : null
}

const CatalogGridData = ({
    maxContent = false,
    children,
    // layout,
    // align,
    // size,
    // flex,
    types = [],
    expandIndicator,
    ...rest
}) => {
    const [showTypes, setShowTypes] = useToggle(false)
    const className = util.joinClasses('catalog-grid__row__data', maxContent && 'max-content')
    return (
        <Grid.Col data-testid={'catalog-grid-data'} className={className} {...rest}>
            <Grid.Row alignItems={'center'}>
                {children}
                {types.length
                    ? expandIndicator({
                          onClick: setShowTypes,
                          isExpanded: showTypes,
                      })
                    : null}
            </Grid.Row>
            <CatalogAccordion types={types} showTypes={showTypes} />
        </Grid.Col>
    )
}

const Footer = ({ children }) => {
    const { rtl } = useContext(LanguageContext)
    return (
        <div data-testid={'catalog-footer'} className={`catalog__footer ${rtl && 'rtl'}`}>
            {children}
        </div>
    )
}

Catalog.Footer = memo(Footer)
Catalog.Grid = CatalogGrid
Catalog.GridRow = CatalogGridRow
Catalog.GridData = CatalogGridData

CatalogGrid.propTypes = {
    children: PropTypes.node,
}

CatalogGridRow.propTypes = {
    children: PropTypes.node,
}

CatalogGridData.propTypes = {
    children: PropTypes.node,
    align: PropTypes.oneOf(['right']),
    flex: PropTypes.bool,
}

Footer.propTypes = {
    seeAllClickHandler: PropTypes.func,
}

Catalog.propTypes = {
    title: PropTypes.string,
    renderOptions: PropTypes.func,
    children: PropTypes.node,
    loading: PropTypes.bool,
}

export default Catalog
